import LazyWidgetController from "./lazy_widget_controller"

// Stimulus controller wrapping Toast UI Editor for the .md-edit-field textarea.
//
// Lifecycle (lazy import + Turbo-cache-safe teardown) is handled by
// LazyWidgetController. markdown-editor does NOT re-parent its textarea (it
// inserts a sibling editor container and hides the textarea), so it can't loop;
// it overrides destroy() to undo the container, unhide the textarea, and detach
// the submit listener. build() returns null (declines) when the field has no id.
//
// Behaviour (unchanged from the legacy `initMarkdownEditors`):
//   - creates a sibling <div id="<fieldId>_editor" class="toastui-md-editor">
//   - height derives from size classes: small/size200 -> 250/200px,
//     size300 -> 300px, size400/default -> 400px, size500 -> 500px
//   - reads data-image-upload-url and data-max-upload-size (default 15 MB)
//   - addImageBlobHook posts the blob (with CSRF token) to the upload URL,
//     enforces the size limit, appends the returned id to #editor_upload_ids_<method>
//   - on form submit, the textarea value is set to editor.getMarkdown()
//
// Usage in views (rendered by AppFormBuilder app_markdown_area):
//   <textarea data-controller="markdown-editor" class="md-edit-field size300"
//             id="product_description" name="product[description]"
//             data-image-upload-url="/uploads" data-max-upload-size="10"></textarea>
//
export default class extends LazyWidgetController {
  async load() {
    return (await import("@toast-ui/editor")).default
  }

  build(Editor) {
    const field = this.element

    const fieldId = field.getAttribute('id')
    if (!(fieldId && fieldId.length)) return null
    if (document.getElementById(fieldId + '_editor')) return null

    // Create the editor container right after the textarea, then hide the textarea.
    const editorElement = document.createElement('div')
    editorElement.id = fieldId + '_editor'
    editorElement.className = 'toastui-md-editor'
    field.insertAdjacentElement('afterend', editorElement)
    field.style.display = 'none'
    this.editorElement = editorElement

    const form = field.closest('form')
    const autofocusEl = form ? form.querySelector('[autofocus]') : null
    const prevScrollY = window.scrollY

    let height = '400px'
    if (field.classList.contains('small')) { height = '250px' }
    if (field.classList.contains('size200')) { height = '200px' }
    if (field.classList.contains('size300')) { height = '300px' }
    if (field.classList.contains('size400')) { height = '400px' }
    if (field.classList.contains('size500')) { height = '500px' }

    const uploadUrl = field.dataset.imageUploadUrl
    const maxSizeMB = field.dataset.maxUploadSize || 15
    const fieldName = field.getAttribute('name')
    // Extract the method name from a field name like "product[description]".
    const methodMatch = fieldName ? fieldName.match(/\[(\w+)\]$/) : null
    const methodName = methodMatch ? methodMatch[1] : null
    const uploadIdsField = (methodName && form)
      ? form.querySelector('#editor_upload_ids_' + methodName)
      : null

    const editorOptions = {
      el: editorElement,
      initialValue: field.value,
      height: height,
      initialEditType: 'markdown',
      usageStatistics: false,
      autofocus: false,
    }

    if (uploadUrl) {
      editorOptions.hooks = {
        addImageBlobHook: function (blob, callback) {
          if (blob.size > maxSizeMB * 1024 * 1024) {
            alert('Image is too large (max ' + maxSizeMB + 'MB)') // nosemgrep
            return
          }

          var formData = new FormData()
          formData.append('image', blob)

          var csrfToken = document.querySelector('meta[name="csrf-token"]')
          var headers = {}
          if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken.content
          }

          fetch(uploadUrl, {
            method: 'POST',
            headers: headers,
            body: formData
          })
            .then(function (response) {
              if (!response.ok) {
                return response.json().then(function (data) {
                  throw new Error((data.errors || ['Upload failed']).join(', '))
                })
              }
              return response.json()
            })
            .then(function (data) {
              callback(data.url, blob.name || 'image')
              if (uploadIdsField && data.id) {
                var current = uploadIdsField.value
                uploadIdsField.value = current ? current + ',' + data.id : String(data.id)
              }
            })
            .catch(function (error) {
              console.error('Image upload failed:', error)
              alert('Image upload failed: ' + error.message) // nosemgrep
            })
        }
      }
    }

    const editor = new Editor(editorOptions)

    if (autofocusEl) {
      setTimeout(function () {
        try { autofocusEl.focus() } catch (e) {}
        try { window.scrollTo(window.scrollX, prevScrollY) } catch (e) {}
      }, 0)
    }

    if (form) {
      this.form = form
      this.onSubmit = () => {
        field.value = editor.getMarkdown()
      }
      form.addEventListener('submit', this.onSubmit)
    }

    return editor
  }

  destroy(editor) {
    if (this.form && this.onSubmit) {
      this.form.removeEventListener('submit', this.onSubmit)
    }
    this.form = null
    this.onSubmit = null

    try { editor.destroy() } catch (e) { /* already gone */ }

    if (this.editorElement && this.editorElement.parentNode) {
      this.editorElement.parentNode.removeChild(this.editorElement)
    }
    this.editorElement = null

    // Unhide the original textarea.
    this.element.style.display = ''
  }
}
